import { useState } from "react";
import { ImageIcon, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { errMsg } from "@/lib/api";
import { useEditor } from "./useEditorState";

const SOURCE = { client: ["Foto del cliente", "text-emerald-700"], ai: ["AI", "text-sky-700"], reuse: ["Riuso", "text-amber-700"],
  fallback: ["Grafica", "text-slate-500"], none: ["Esclusa", "text-slate-400"] };
const ROLE = { hero: "Apertura", background: "Sfondo", feature: "Sezione", card: "Scheda", gallery: "Galleria" };
const human = (t) => (t || "").replace(/([a-zà-ù])([A-Z])/g, "$1 $2");
const usd3 = (n) => `${n.toLocaleString("it-IT", { minimumFractionDigits: 3, maximumFractionDigits: 3 })} $`;

function Totals({ p }) {
  const t = p.totals;
  return (
    <div className="space-y-1" data-testid="image-plan-totals">
      <p><b className="text-slate-900">{t.slots}</b> posizioni · {t.client} foto del cliente · {t.ai} AI ({t.ai_hd} in alta risoluzione) · {t.reuse} riusi · {t.fallback} grafiche</p>
      <p data-testid="image-plan-cost">Stima {usd3(t.usd_total)} (immagini {usd3(t.usd_images)} + controlli e rifacimenti {usd3(t.usd_qa)}) · budget {usd3(p.budget_usd)}</p>
      <p className="text-slate-500" data-testid="image-plan-budget-rule">Budget di questo sito {usd3(p.budget_usd)}: iniziale {usd3(p.budget_base_usd)} più l'aggiunta per le pagine in più, tetto {usd3(p.budget_cap_usd)}</p>
      {(p.warnings || []).map((w) => <p key={w} className="text-amber-800" data-testid="image-plan-warning">{w}</p>)}
    </div>
  );
}

function PageList({ slots }) {
  const pages = [...new Set(slots.map((s) => s.page))];
  return (
    <div className="space-y-2 max-h-72 overflow-auto" data-testid="image-plan-pages">
      {pages.map((pg) => (
        <div key={pg}>
          <p className="font-medium text-slate-800">{pg}</p>
          <ul className="mt-0.5 space-y-0.5">
            {slots.filter((s) => s.page === pg).map((s) => (
              <li key={s.id} className="flex justify-between gap-2" title={`${ROLE[s.role]}${s.n ? ` ${s.n}` : ""} · ${s.section} · ${s.width}×${s.height} — ${s.reason}`} data-testid={`image-plan-slot-${s.id}`}>
                <span className="min-w-0"><span className="block truncate text-slate-800">{human(s.section)}</span>
                  <span className="block text-slate-500">{ROLE[s.role]}{s.n ? ` ${s.n}` : ""} · {s.width}×{s.height}</span></span>
                <span className={`shrink-0 text-right ${SOURCE[s.source][1]}`}>{SOURCE[s.source][0]}{s.usd ? <span className="block">{usd3(s.usd)}</span> : null}</span>
              </li>))}
          </ul>
        </div>))}
    </div>
  );
}

// Read-only image plan (phase 1): positions, sources and cost computed by the server; nothing is generated.
export default function ImagePlanPanel() {
  const { api, project } = useEditor();
  const [state, setState] = useState({ loading: false, data: null, error: "" });
  const load = async () => {
    setState({ loading: true, data: null, error: "" });
    try { setState({ loading: false, data: (await api.get(`/projects/${project.id}/image-plan`)).data, error: "" }); }
    catch (e) { setState({ loading: false, data: null, error: errMsg(e) }); }
  };
  const d = state.data;
  return (
    <div className="rounded-lg border bg-white p-3 space-y-2 text-[11px] text-slate-700" data-testid="image-plan-panel">
      <p className="text-[11px] uppercase tracking-wider text-slate-500 flex items-center gap-1.5"><ImageIcon size={12} />Piano immagini</p>
      <p className="text-slate-500">Anteprima di quali foto servono, da dove arrivano e quanto costerebbero. Non genera immagini e non ha costi.</p>
      <Button variant="outline" size="sm" className="w-full" onClick={load} disabled={state.loading} data-testid="image-plan-load-btn">
        {state.loading && <Loader2 size={13} className="mr-1.5 animate-spin" />}{d ? "Ricalcola" : "Calcola piano immagini"}
      </Button>
      {state.error && <p className="text-red-700" data-testid="image-plan-error">{state.error}</p>}
      {d && !d.available && <p className="text-slate-500" data-testid="image-plan-unavailable">{d.reason}</p>}
      {d?.available && <><Totals p={d.plan} /><PageList slots={d.plan.slots} /></>}
    </div>
  );
}
